import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Collapse } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import { useUser } from "../contexts/UserContext";

// Check role permission
const hasPermission = (requiredRoles, userRole) => {
  if (!requiredRoles) return true;
  return requiredRoles.includes(userRole);
};

function NavItem({ to, icon, label, onClick }) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <li className="nav-item">
      <Link
        to={to}
        onClick={onClick}
        className={`nav-link d-flex align-items-center py-3 px-3 transition-all ${
          isActive
            ? "bg-inchworm text-dark fw-semibold border-start border-3 border-primary"
            : "text-dark-hover"
        }`}
      >
        <span
          className="me-3 d-flex align-items-center"
          style={{ width: "24px" }}
        >
          {icon}
        </span>
        {label}
      </Link>
    </li>
  );
}

function Submenu({ id, icon, label, items, isActive, toggleMenu, onClick }) {
  const location = useLocation();

  return (
    <li className="nav-item" key={id}>
      <button
        className="nav-link text-dark-hover d-flex justify-content-between align-items-center w-100 py-3 px-3 transition-all"
        onClick={() => toggleMenu(id)}
        aria-expanded={isActive}
      >
        <span className="d-flex align-items-center">
          <span
            className="me-3 d-flex align-items-center"
            style={{ width: "24px" }}
          >
            {icon}
          </span>
          {label}
        </span>
        <i
          className={`bi transition-all ${
            isActive ? "bi-chevron-up" : "bi-chevron-down"
          }`}
        ></i>
      </button>

      <Collapse in={isActive}>
        <div className="bg-light-accent">
          <ul className="list-unstyled fw-normal small py-2">
            {items.map((item, index) => {
              const isActiveChild = location.pathname === item.to;
              return (
                <li key={index}>
                  <Link
                    to={item.to}
                    onClick={onClick}
                    className={`nav-link d-flex align-items-center py-2 ps-5 pe-3 transition-all ${
                      isActiveChild
                        ? "bg-inchworm text-dark fw-semibold border-start border-3 border-primary"
                        : "text-muted text-dark-hover"
                    }`}
                  >
                    {item.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </Collapse>
    </li>
  );
}

export default function AccountSidebar({ onNavigate }) {
  const { user, loading } = useUser();
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();

  if (loading)
    return (
      <div className="d-flex justify-content-center align-items-center h-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (!user)
    return (
      <div className="p-3 text-center">
        <p className="text-muted">Not authenticated</p>
      </div>
    );

  const toggleMenu = (menuId) => {
    setOpenMenus((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = false;
      });
      newState[menuId] = !prev[menuId];
      return newState;
    });
  };

  const closeAllMenus = () => {
    setOpenMenus({});
  };

  const handleNavClick = () => {
    closeAllMenus();
    if (onNavigate) onNavigate();
  };

  const navItems = [
    {
      type: "item",
      to: "/account",
      icon: <i className="bi bi-house"></i>,
      label: "Dashboard",
      roles: ["user", "superadmin", "admin"],
    },
    {
      type: "item",
      to: "/account/progress",
      icon: <i className="bi bi-mortarboard"></i>,
      label: "Progress",
      roles: ["user", "superadmin", "admin"],
    },
    {
      type: "submenu",
      id: "profileMenu",
      icon: <i className="bi bi-person"></i>,
      label: "Profile Settings",
      items: [
        { to: "/account/manage", label: "Login and Security" },
        { to: "/account/subscription", label: "Subscription" },
      ],
      roles: ["user", "superadmin", "admin"],
    },
    {
      type: "submenu",
      id: "DistMenu",
      icon: <i className="bi bi-cash-coin"></i>,
      label: "Affiliate Zone",
      items: [{ to: "/account/aff-dashboard", label: "Dashboard" }],
      roles: ["distributor"],
    },
  ];

  return (
    <nav className="d-flex flex-column h-100 bg-white shadow-sm">
      {/* Sidebar header */}
      <div className="p-3 border-bottom">
        <div className="d-flex align-items-center">
          <div className="flex-grow-1">
            <h6 className="mb-0 text-dark">Hello,</h6>
            <small className="text-muted">{user.name || user.email}</small>
          </div>
          <i className="bi bi-person-circle fs-4 text-primary"></i>
        </div>
      </div>

      {/* Navigation items */}
      <ul className="nav flex-column flex-grow-1 pt-2">
        {navItems.map((item) => {
          if (!hasPermission(item.roles, user.role)) return null;

          if (item.type === "item") {
            return (
              <NavItem
                key={item.to}
                to={item.to}
                icon={item.icon}
                label={item.label}
                onClick={handleNavClick}
              />
            );
          } else if (item.type === "submenu") {
            const isActive =
              openMenus[item.id] ||
              item.items.some((child) => location.pathname === child.to);
            return (
              <Submenu
                key={item.id}
                id={item.id}
                icon={item.icon}
                label={item.label}
                items={item.items}
                isActive={isActive}
                toggleMenu={toggleMenu}
                onClick={handleNavClick}
              />
            );
          }
          return null;
        })}
      </ul>
    </nav>
  );
}
